import SpinLoading from '../spinLoading'
import UploadImage from '../uploadImage'
import { InboxOutlined } from '@ant-design/icons'
import { Upload } from 'antd'
import type { RcFile, UploadProps } from 'antd/es/upload'
import { IImageUpload } from 'interfaces'
import { useEffect, useRef, useState } from 'react'
import { useFileService } from 'services'
import styled from 'styled-components'
import { debounce } from 'utils'

export const StyledUpload = styled(Upload.Dragger)`
  font-size: 40px;
  height: 102px !important;
  width: 102px !important;
  .ant-upload-btn {
    height: 102px !important;
    width: 102px !important;
    padding: 16px !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    .ant-upload-drag-icon {
      margin-bottom: 0 !important;
    }
  }
`
interface IProps {
  max: number
  imgs?: Array<IImageUpload>
  disabled?: boolean
  onChange?: (imgs?: Array<IImageUpload>) => void
}

export const UploadMultipleImages = (props: IProps) => {
  const { max, onChange, imgs, disabled } = props
  const onRemoveRef = useRef(false)
  const [loading, setLoading] = useState(false)
  const [images, setImages] = useState<Array<IImageUpload>>(imgs || [])
  const { postFilesToServer } = useFileService()

  useEffect(() => {
    if (imgs) {
      setImages(imgs)
    }
  }, [imgs])
  const getBase64AndArrayBuffer = (
    file: RcFile
  ): Promise<{ base64: string; arrayBuffer: ArrayBuffer }> => {
    return new Promise((resolve, reject) => {
      if (!file) return undefined
      const readerBase64 = new FileReader()
      const readerArrayBuffer = new FileReader()
      let base64Result: string
      let arrayBufferResult: ArrayBuffer
      readerBase64.onload = () => {
        base64Result = readerBase64.result as string
        if (base64Result && arrayBufferResult) {
          resolve({ base64: base64Result, arrayBuffer: arrayBufferResult })
        }
      }
      readerArrayBuffer.onload = () => {
        arrayBufferResult = readerArrayBuffer.result as ArrayBuffer
        if (base64Result && arrayBufferResult) {
          resolve({ base64: base64Result, arrayBuffer: arrayBufferResult })
        }
      }
      readerBase64.onerror = readerArrayBuffer.onerror = (error: any) =>
        reject(error)
      readerBase64.readAsDataURL(file)
      readerArrayBuffer.readAsArrayBuffer(file)
    })
  }

  const handleUploadFiles = debounce(async (newFileList: Array<any>) => {
    if (onRemoveRef.current) {
      onRemoveRef.current = false
      return
    }
    setLoading(true)
    const arrayRequest: any = []

    try {
      for (let index = 0; index < newFileList.length; index++) {
        const file = newFileList[index]
        const { base64, arrayBuffer } = await getBase64AndArrayBuffer(
          file?.originFileObj as RcFile
        )
        let fileType = file.type
        const arr = new Uint8Array(arrayBuffer).subarray(0, 4)
        let header = ''
        for (let i = 0; i < arr.length; i++) {
          header += arr[i].toString(16)
        }
        switch (header) {
          case '89504e47':
            fileType = 'image/png'
            break
          case 'ffd8ffe0':
          case 'ffd8ffe1':
          case 'ffd8ffe2':
          case 'ffd8ffe3':
          case 'ffd8ffe8':
            fileType = 'image/jpeg'
            break
          default:
            fileType = 'image/png'
            break
        }
        const res = await postFilesToServer([
          {
            type: 0,
            mime_type: fileType || '',
            size: file.size || 0,
            data: base64?.split(',')[1]
          }
        ])

        if (res?._successfully) {
          arrayRequest.push(res?.data?.[0])
        }
        if (index === newFileList.length - 1 && arrayRequest.length > 0) {
          const newImages = arrayRequest?.map((img: any) => ({
            ...img,
            uid: file.uid
          }))
          const imgs = [...images, ...newImages]
          if (onChange) onChange(imgs)
          setImages(imgs)
        }
      }
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }, 100)
  const handleChange: UploadProps['onChange'] = async ({
    fileList: newFileList
  }) => {
    if (newFileList?.length === 0 && onChange) {
      onChange([])
      return
    }
    handleUploadFiles(newFileList)
  }

  const handleRemoveImage = (img: IImageUpload) => {
    const newImgs = images?.filter((i) => i.url !== img.url)
    setImages(newImgs)
    if (onChange) onChange(newImgs as any)
  }

  return (
    <>
      {images?.map((img) => {
        return (
          <UploadImage
            hideUpload
            listImage={images?.map((img) => img.url)}
            preventRemove={disabled}
            key={img.id}
            value={img.url}
            onRemove={() => handleRemoveImage(img)}
          />
        )
      })}

      {images?.length < max && (
        <SpinLoading loading={loading}>
          <StyledUpload
            beforeUpload={() => false}
            accept=".png, .jpg, .jpeg"
            multiple={true}
            maxCount={max ? max - images.length : undefined}
            disabled={disabled}
            showUploadList={false}
            fileList={[]}
            onChange={handleChange}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
          </StyledUpload>
        </SpinLoading>
      )}
    </>
  )
}
